import styles from "./WorkDetails.module.scss";
import { useState, useRef, useEffect, useCallback } from "react";
import { ReactComponent as ArrowLeftIcon } from "../../../../assets/images/arrowLeft.svg";
import { ReactComponent as GithubIcon } from "../../../../assets/images/githubIcon.svg";
import { ReactComponent as ExternalIcon } from "../../../../assets/images/externalLinkIcon.svg";
import { ReactComponent as ChevronLeft } from "../../../../assets/images/chevronLeft.svg";
import { ReactComponent as ChevronRight } from "../../../../assets/images/chevronRight.svg";
import PreviewSlide from "./children/previewSlide/PreviewSlide";
import projects from "../../../../api/projects.json";
import { useNavigate, useParams } from "react-router-dom";
import WorkDetailsNotFound from "./children/workDetailsNotFound/WorkDetailsNotFound";

function WorkDetails() {
  const [idx, setIdx] = useState(0);
  const navigate = useNavigate();
  const { id } = useParams();
  const project = projects.find((p) => p.id === Number(id));

  const touchX = useRef(null);
  const total = project?.previews?.length;

  const prev = useCallback(
    () => setIdx((i) => (i - 1 + total) % total),
    [total],
  );
  const next = useCallback(() => setIdx((i) => (i + 1) % total), [total]);

  // auto-advance slider
  useEffect(() => {
    const t = setInterval(next, 3500);
    return () => clearInterval(t);
  }, [next]);

  // keyboard
  useEffect(() => {
    const h = (e) => {
      if (e.key === "ArrowLeft") prev();
      if (e.key === "ArrowRight") next();
    };
    window.addEventListener("keydown", h);
    return () => window.removeEventListener("keydown", h);
  }, [prev, next]);

  // swipe
  const onTS = (e) => {
    touchX.current = e.touches[0].clientX;
  };
  const onTE = (e) => {
    if (touchX.current === null) return;
    const d = touchX.current - e.changedTouches[0].clientX;
    if (Math.abs(d) > 40) d > 0 ? next() : prev();
    touchX.current = null;
  };

  useEffect(() => {
    window.scrollTo({ top: 0, behavior: "smooth" });
  }, []);

  if (!project) return <WorkDetailsNotFound />;

  return (
    <div className={styles["dp-root"]}>
      {/* ── hero ── */}
      <div className={styles["dp-hero"]}>
        <div className={styles["dp-topbar"]}>
          <button
            className={styles["dp-back-btn"]}
            onClick={() => {
              navigate("/#projects");
            }}
          >
            <ArrowLeftIcon /> Back to Projects
          </button>
        </div>

        <div className={styles["dp-hero-grid"]}>
          {/* info */}
          <div className={styles["dp-hero-info"]}>
            <div className={styles["dp-eyebrow"]}>
              <span
                style={{
                  fontSize: "0.7rem",
                  fontWeight: 700,
                  letterSpacing: "0.1em",
                  textTransform: "uppercase",
                  color: "#f5a623",
                }}
              >
                {project.category}
              </span>
              <span style={{ color: "rgba(255,255,255,0.3)" }}>•</span>
              <span
                style={{ fontSize: "0.7rem", color: "rgba(255,255,255,0.5)" }}
              >
                {project.year}
              </span>
            </div>
            <h1 className={styles["dp-title"]}>{project.title}</h1>
            <p className={styles["dp-subtitle"]}>{project.subtitle}</p>
            <p className={styles["dp-desc"]}>{project.description}</p>
            <div className={styles["dp-tags"]}>
              {project.tags.map((t) => (
                <span key={t} className={styles["dp-tag"]}>
                  {t}
                </span>
              ))}
            </div>
            <div className={styles["dp-btns"]}>
              {project.github && project.github !== "#" && (
                <a href={project.github} className={styles["dp-btn-w"]}>
                  <GithubIcon /> View Code
                </a>
              )}
              {project.demo && project.demo !== "#" && (
                <a href={project.demo} className={styles["dp-btn-g"]}>
                  <ExternalIcon /> Live Demo
                </a>
              )}
            </div>
          </div>

          {/* slider */}
          <div className={styles["dp-hero-slider"]}>
            <div
              className={styles["dp-slider-wrap"]}
              onTouchStart={onTS}
              onTouchEnd={onTE}
            >
              {project.previews.map((p, i) => (
                <PreviewSlide key={i} preview={p} active={i === idx} />
              ))}
            </div>
            <div className={styles["dp-slide-ctrl"]}>
              <button className={styles["dp-arrow"]} onClick={prev}>
                <ChevronLeft />
              </button>
              <div style={{ display: "flex", gap: 7, alignItems: "center" }}>
                {project.previews.map((_, i) => (
                  <button
                    key={i}
                    className={styles["dp-dot"]}
                    onClick={() => setIdx(i)}
                    style={{
                      width: i === idx ? 22 : 7,
                      background:
                        i === idx ? "#f5a623" : "rgba(255,255,255,0.3)",
                    }}
                  />
                ))}
              </div>
              <button className={styles["dp-arrow"]} onClick={next}>
                <ChevronRight />
              </button>
            </div>
            <p className={styles["dp-hint"]}>
              {idx + 1} / {total}
            </p>
          </div>
        </div>
      </div>

      {/* ── body ── */}
      <div className={styles["dp-body"]}>
        {/* metrics */}
        <div className={styles["dp-metrics"]}>
          {project.metrics.map((m) => (
            <div key={m.label} className={styles["dp-m-card"]}>
              <div className={styles["dp-m-val"]}>{m.value}</div>
              <div className={styles["dp-m-lbl"]}>{m.label}</div>
            </div>
          ))}
        </div>

        {/* content cards */}
        <div className={styles["dp-content"]}>
          {/* About */}
          <div className={styles["dp-ccard"]}>
            <h3 className={styles["dp-ctitle"]}>
              <span
                className={styles["dp-cicon"]}
                style={{ background: "#fff8e7" }}
              >
                📖
              </span>
              About the Project
            </h3>
            <p>{project.longDescription}</p>
          </div>

          {/* Challenge + Outcome */}
          <div className={styles["dp-right-col"]}>
            <div className={styles["dp-ccard"]}>
              <h3 className={styles["dp-ctitle"]}>
                <span
                  className={styles["dp-cicon"]}
                  style={{ background: "#fff0f0" }}
                >
                  ⚡
                </span>
                Key Challenge
              </h3>
              <p>{project.challenges}</p>
            </div>
            <div className={styles["dp-ocard"]}>
              <h3 className={styles["dp-ctitle"]}>
                <span
                  className={styles["dp-cicon"]}
                  style={{ background: "rgba(245,166,35,0.2)" }}
                >
                  🏆
                </span>
                Outcome
              </h3>
              <p>{project.outcome}</p>
            </div>
          </div>
        </div>

        {/* tech stack */}
        <div className={styles["dp-stack"]}>
          <h3 className={styles["dp-s-title"]}>Tech Stack</h3>
          <div className={styles["dp-s-tags"]}>
            {project.tags.map((t) => (
              <span key={t} className={styles["dp-s-tag"]}>
                {t}
              </span>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}

export default WorkDetails;
